import React, { useState, useEffect } from 'react';
import './Exercises.css';

const Exercises = ({ onExercisesComplete, onBack }) => {
  const [exercises, setExercises] = useState({
    exercise1: {
      country: '',
      priority1: '',
      paranoia1: '',
      prohibition1: '',
      priority2: '',
      paranoia2: '',
      prohibition2: '',
      priority3: '',
      paranoia3: '',
      prohibition3: ''
    },
    exercise2: {
      jobs: '',
      salaryExpenses: '',
      technologyCosts: '',
      exportRevenues: '',
      depreciation: '',
      buildingCosts: '',
      landRedevelopment: '',
      constructionExpenses: ''
    },
    exercise3: {
      builders: ['', '', ''],
      enablers: ['', '', ''],
      thrivers: ['', '', ''],
      supportees: ['', '', '']
    },
    exercise4: {
      industry: '',
      country1: { name: '', taxRate: '', painEndured: '', oddsOfSuccess: '', taxIncentive: '', trueTaxCost: '' },
      country2: { name: '', taxRate: '', painEndured: '', oddsOfSuccess: '', taxIncentive: '', trueTaxCost: '' },
      country3: { name: '', taxRate: '', painEndured: '', oddsOfSuccess: '', taxIncentive: '', trueTaxCost: '' }
    },
    exercise5: {
      country: '',
      currency1: '',
      currency2: '',
      currency3: ''
    },
    exercise6: {
      age10: '',
      age20: '',
      age30: '',
      age40: '',
      age50: '',
      age60: '',
      age70: '',
      age80: ''
    },
    exercise7: {
      question1: 'b',
      question2: 'a',
      question3: true,
      question4: 'c',
      question5: 'c'
    }
  });

  const [currentExercise, setCurrentExercise] = useState(null);
  const [completionStatus, setCompletionStatus] = useState({});
  const [userEmail, setUserEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState({ type: '', message: '' });
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Load user email from localStorage if available
  useEffect(() => {
    const savedEmail = localStorage.getItem('userEmail');
    if (savedEmail) {
      setUserEmail(savedEmail);
    }
  }, []);

  // Save user email to localStorage
  useEffect(() => {
    if (userEmail) {
      localStorage.setItem('userEmail', userEmail);
    }
  }, [userEmail]);

  // Send progress email when an exercise is completed
  const sendProgressEmail = async (exerciseId, exerciseData) => {
    if (!userEmail.trim()) {
      setEmailStatus({ type: 'warning', message: 'Please enter your email address to receive progress updates.' });
      return;
    }

    setIsSendingEmail(true);
    setEmailStatus({ type: '', message: '' });

    try {
      // Create progress summary
      const progressData = {
        exerciseId: exerciseId,
        exerciseTitle: exerciseMetadata.find(ex => ex.id === exerciseId)?.title || `Exercise ${exerciseId}`,
        completedExercises: Object.keys(completionStatus).filter(key => completionStatus[key]).length,
        totalExercises: 7,
        completionPercentage: Math.round((Object.keys(completionStatus).filter(key => completionStatus[key]).length / 7) * 100),
        currentExerciseData: exerciseData,
        allExercisesStatus: completionStatus
      };

      // Send email via server
      const response = await fetch('http://localhost:5001/api/send-progress-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail.trim(),
          progressData: progressData
        }),
      });

      const result = await response.json();

      if (result.success) {
        setEmailStatus({ 
          type: 'success', 
          message: `✅ Progress email sent to ${userEmail}! Exercise ${exerciseId} completed.` 
        });
        // Clear success message after 5 seconds
        setTimeout(() => setEmailStatus({ type: '', message: '' }), 5000);
      } else {
        throw new Error(result.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Email error:', error);
      setEmailStatus({ 
        type: 'error', 
        message: `❌ Failed to send progress email: ${error.message}. Please check if the server is running.` 
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Exercise metadata for the classroom interface
  const exerciseMetadata = [
    {
      id: 1,
      title: 'The 3P Framework',
      description: 'Choose a country and analyze its Priority, Paranoia, and Prohibition focus areas',
      icon: '🌍',
      color: '#4285f4',
      estimatedTime: '10-15 min'
    },
    {
      id: 2,
      title: 'Contribution Account',
      description: 'Translate business expenses into government-valued contribution language',
      icon: '💰',
      color: '#fbbc04',
      estimatedTime: '12-18 min'
    },
    {
      id: 3,
      title: 'The BETS Framework',
      description: 'Identify examples of Builders, Enablers, Thrivers, and Supportees',
      icon: '🏗️',
      color: '#ea4335',
      estimatedTime: '15-20 min'
    },
    {
      id: 4,
      title: 'True Tax Cost',
      description: 'Compare countries using the True Tax Cost formula across different industries',
      icon: '🧮',
      color: '#9c27b0',
      estimatedTime: '20-25 min'
    },
    {
      id: 5,
      title: 'Your Government\'s Currency',
      description: 'Identify the top three currencies your government values most',
      icon: '🏛️',
      color: '#ff9800',
      estimatedTime: '8-10 min'
    },
    {
      id: 6,
      title: 'Your Currency Over Life Stages',
      description: 'Reflect on what mattered most at different ages in your life',
      icon: '⏰',
      color: '#607d8b',
      estimatedTime: '10-15 min'
    },
    {
      id: 7,
      title: 'Bulletproof Tax Structure Quiz',
      description: 'Test your understanding with interactive quiz questions',
      icon: '✅',
      color: '#4caf50',
      estimatedTime: '5-8 min'
    }
  ];

  // Load exercises data from localStorage
  useEffect(() => {
    const savedExercises = localStorage.getItem('taxAssessmentExercises');
    if (savedExercises) {
      try {
        setExercises(JSON.parse(savedExercises));
      } catch (error) {
        console.error('Error loading exercises:', error);
      }
    }
  }, []);

  // Save exercises data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('taxAssessmentExercises', JSON.stringify(exercises));
    updateCompletionStatus();
  }, [exercises]);

  // Update completion status and send email when exercise is completed
  const updateCompletionStatus = () => {
    const newStatus = {};
    let hasChanges = false;

    Object.keys(exercises).forEach(key => {
      const isComplete = isExerciseComplete(key);
      if (completionStatus[key] !== isComplete) {
        hasChanges = true;
      }
      newStatus[key] = isComplete;
    });

    if (hasChanges) {
      setCompletionStatus(newStatus);
      
      // Find which exercise was just completed
      Object.keys(newStatus).forEach(key => {
        if (newStatus[key] && !completionStatus[key]) {
          // This exercise was just completed, send email
          const exerciseId = parseInt(key.replace('exercise', ''));
          sendProgressEmail(exerciseId, exercises[key]);
        }
      });
    }
  };

  const isExerciseComplete = (exerciseKey) => {
    const exercise = exercises[exerciseKey];
    switch (exerciseKey) {
      case 'exercise1':
        return exercise.country && exercise.priority1 && exercise.paranoia1 && exercise.prohibition1;
      case 'exercise2':
        return exercise.jobs || exercise.salaryExpenses || exercise.technologyCosts || exercise.exportRevenues;
      case 'exercise3':
        return exercise.builders.some(b => b) || exercise.enablers.some(e => e) || 
               exercise.thrivers.some(t => t) || exercise.supportees.some(s => s);
      case 'exercise4':
        return exercise.industry && exercise.country1.name;
      case 'exercise5':
        return exercise.country && exercise.currency1;
      case 'exercise6':
        return exercise.age10 || exercise.age20 || exercise.age30 || exercise.age40;
      case 'exercise7':
        return true; // Quiz is always complete
      default:
        return false;
    }
  };

  const handleExerciseChange = (exerciseKey, field, value) => {
    setExercises(prev => ({
      ...prev,
      [exerciseKey]: {
        ...prev[exerciseKey],
        [field]: value
      }
    }));
  };

  const handleArrayChange = (exerciseKey, field, index, value) => {
    setExercises(prev => ({
      ...prev,
      [exerciseKey]: {
        ...prev[exerciseKey],
        [field]: prev[exerciseKey][field].map((item, i) => i === index ? value : item)
      }
    }));
  };

  const handleCountryChange = (exerciseKey, index, field, value) => {
    setExercises(prev => ({
      ...prev,
      [exerciseKey]: {
        ...prev[exerciseKey],
        countries: prev[exerciseKey].countries.map((country, i) => 
          i === index ? { ...country, [field]: value } : country
        )
      }
    }));
  };

  const handleExercise5Change = (countryIndex, field, value) => {
    setExercises(prev => ({
      ...prev,
      exercise5: {
        ...prev.exercise5,
        [`country${countryIndex + 1}`]: {
          ...prev.exercise5[`country${countryIndex + 1}`],
          [field]: value
        }
      }
    }));
  };

  const calculateTrueTaxCost = (countryIndex) => {
    const country = exercises.exercise5[`country${countryIndex + 1}`];
    // Check if values are defined and not empty strings (allowing 0 as valid input)
    if (country.taxRate !== '' && country.painEndured !== '' && country.oddsOfSuccess !== '' && country.taxIncentive !== '') {
      const taxRate = parseFloat(country.taxRate) || 0;
      const painEndured = parseFloat(country.painEndured) || 0;
      const oddsOfSuccess = parseFloat(country.oddsOfSuccess) || 0;
      const taxIncentive = parseFloat(country.taxIncentive) || 0;
      
      const trueTaxCost = taxRate + painEndured - oddsOfSuccess - taxIncentive;
      
      handleExercise5Change(countryIndex, 'trueTaxCost', trueTaxCost.toFixed(2));
    }
  };

  const getCompletionPercentage = () => {
    const completed = Object.values(completionStatus).filter(Boolean).length;
    return Math.round((completed / Object.keys(completionStatus).length) * 100);
  };

  const openExercise = (exerciseId) => {
    setCurrentExercise(exerciseId);
  };

  const closeExercise = () => {
    setCurrentExercise(null);
  };

  const renderExercise1 = () => (
    <div className="exercise-section">
      <div className="exercise-header">
        <h3>Exercise 1 – The 3P Framework</h3>
        <button onClick={closeExercise} className="close-exercise-btn">✕</button>
      </div>
      <p className="exercise-instruction">
        Choose a country and, based on your perception, fill in its Priority, Paranoia, and Prohibition focus areas.
      </p>
      
      <div className="exercise-input-group">
        <label>Country:</label>
        <input
          type="text"
          value={exercises.exercise1.country}
          onChange={(e) => handleExerciseChange('exercise1', 'country', e.target.value)}
          placeholder="Enter country name"
        />
      </div>

      <div className="3p-grid">
        <div className="3p-header">
          <div>Priority</div>
          <div>Paranoia</div>
          <div>Prohibition</div>
        </div>
        
        <div className="3p-row">
          <input
            type="text"
            value={exercises.exercise1.priority1}
            onChange={(e) => handleExerciseChange('exercise1', 'priority1', e.target.value)}
            placeholder="Priority 1"
          />
          <input
            type="text"
            value={exercises.exercise1.paranoia1}
            onChange={(e) => handleExerciseChange('exercise1', 'paranoia1', e.target.value)}
            placeholder="Paranoia 1"
          />
          <input
            type="text"
            value={exercises.exercise1.prohibition1}
            onChange={(e) => handleExerciseChange('exercise1', 'prohibition1', e.target.value)}
            placeholder="Prohibition 1"
          />
        </div>

        <div className="3p-row">
          <input
            type="text"
            value={exercises.exercise1.priority2}
            onChange={(e) => handleExerciseChange('exercise1', 'priority2', e.target.value)}
            placeholder="Priority 2"
          />
          <input
            type="text"
            value={exercises.exercise1.paranoia2}
            onChange={(e) => handleExerciseChange('exercise1', 'paranoia2', e.target.value)}
            placeholder="Paranoia 2"
          />
          <input
            type="text"
            value={exercises.exercise1.prohibition2}
            onChange={(e) => handleExerciseChange('exercise1', 'prohibition2', e.target.value)}
            placeholder="Prohibition 2"
          />
        </div>

        <div className="3p-row">
          <input
            type="text"
            value={exercises.exercise1.priority3}
            onChange={(e) => handleExerciseChange('exercise1', 'priority3', e.target.value)}
            placeholder="Priority 3"
          />
          <input
            type="text"
            value={exercises.exercise1.paranoia3}
            onChange={(e) => handleExerciseChange('exercise1', 'paranoia3', e.target.value)}
            placeholder="Paranoia 3"
          />
          <input
            type="text"
            value={exercises.exercise1.prohibition3}
            onChange={(e) => handleExerciseChange('exercise1', 'prohibition3', e.target.value)}
            placeholder="Prohibition 3"
          />
        </div>
      </div>
    </div>
  );

  const renderExercise2 = () => (
    <div className="exercise-section">
      <div className="exercise-header">
        <h3>Exercise 2 – Contribution Account</h3>
        <button onClick={closeExercise} className="close-exercise-btn">✕</button>
      </div>
      <p className="exercise-instruction">
        Translate business expenses into the "language" the government values as contribution.
      </p>
      
      <div className="exercise-table">
        <div className="table-header">
          <div>Financial Language</div>
          <div>Government's Language of Contribution</div>
        </div>
        
        <div className="table-row">
          <div>Jobs</div>
          <input
            type="text"
            value={exercises.exercise2.jobs}
            onChange={(e) => handleExerciseChange('exercise2', 'jobs', e.target.value)}
            placeholder="Employment"
          />
        </div>
        
        <div className="table-row">
          <div>Salary expenses</div>
          <input
            type="text"
            value={exercises.exercise2.salaryExpenses}
            onChange={(e) => handleExerciseChange('exercise2', 'salaryExpenses', e.target.value)}
            placeholder="Livelihood creation"
          />
        </div>
        
        <div className="table-row">
          <div>Technology costs</div>
          <input
            type="text"
            value={exercises.exercise2.technologyCosts}
            onChange={(e) => handleExerciseChange('exercise2', 'technologyCosts', e.target.value)}
            placeholder="Innovation capability"
          />
        </div>
        
        <div className="table-row">
          <div>Export revenues</div>
          <input
            type="text"
            value={exercises.exercise2.exportRevenues}
            onChange={(e) => handleExerciseChange('exercise2', 'exportRevenues', e.target.value)}
            placeholder="Foreign exchange inflow"
          />
        </div>
        
        <div className="table-row">
          <div>Depreciation</div>
          <input
            type="text"
            value={exercises.exercise2.depreciation}
            onChange={(e) => handleExerciseChange('exercise2', 'depreciation', e.target.value)}
            placeholder="Capital investment"
          />
        </div>
        
        <div className="table-row">
          <div>Building costs</div>
          <input
            type="text"
            value={exercises.exercise2.buildingCosts}
            onChange={(e) => handleExerciseChange('exercise2', 'buildingCosts', e.target.value)}
            placeholder="Infrastructure development"
          />
        </div>
        
        <div className="table-row">
          <div>Land redevelopment costs</div>
          <input
            type="text"
            value={exercises.exercise2.landRedevelopment}
            onChange={(e) => handleExerciseChange('exercise2', 'landRedevelopment', e.target.value)}
            placeholder="Urban renewal"
          />
        </div>
        
        <div className="table-row">
          <div>Construction expenses</div>
          <input
            type="text"
            value={exercises.exercise2.constructionExpenses}
            onChange={(e) => handleExerciseChange('exercise2', 'constructionExpenses', e.target.value)}
            placeholder="Public asset creation"
          />
        </div>
      </div>
    </div>
  );

  const renderExercise3 = () => (
    <div className="exercise-section">
      <div className="exercise-header">
        <h3>Exercise 3 – The BETS Framework</h3>
        <button onClick={closeExercise} className="close-exercise-btn">✕</button>
      </div>
      <p className="exercise-instruction">
        For your chosen country, identify examples of Builders, Enablers, Thrivers, and Supportees.
      </p>
      
      <div className="exercise-table">
        <div className="table-header">
          <div>Type</div>
          <div>Example 1</div>
          <div>Example 2</div>
          <div>Example 3</div>
        </div>
        
        <div className="table-row">
          <div>Builder</div>
          {exercises.exercise3.builders.map((example, index) => (
            <input
              key={index}
              type="text"
              value={example}
              onChange={(e) => handleArrayChange('exercise3', 'builders', index, e.target.value)}
              placeholder={`Example ${index + 1}`}
            />
          ))}
        </div>
        
        <div className="table-row">
          <div>Enabler</div>
          {exercises.exercise3.enablers.map((example, index) => (
            <input
              key={index}
              type="text"
              value={example}
              onChange={(e) => handleArrayChange('exercise3', 'enablers', index, e.target.value)}
              placeholder={`Example ${index + 1}`}
            />
          ))}
        </div>
        
        <div className="table-row">
          <div>Thriver</div>
          {exercises.exercise3.thrivers.map((example, index) => (
            <input
              key={index}
              type="text"
              value={example}
              onChange={(e) => handleArrayChange('exercise3', 'thrivers', index, e.target.value)}
              placeholder={`Example ${index + 1}`}
            />
          ))}
        </div>
        
        <div className="table-row">
          <div>Supportee</div>
          {exercises.exercise3.supportees.map((example, index) => (
            <input
              key={index}
              type="text"
              value={example}
              onChange={(e) => handleArrayChange('exercise3', 'supportees', index, e.target.value)}
              placeholder={`Example ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const renderExercise4 = () => (
    <div className="exercise-section">
      <div className="exercise-header">
        <h3>Exercise 4 – True Tax Cost</h3>
        <button onClick={closeExercise} className="close-exercise-btn">✕</button>
      </div>
      <p className="exercise-instruction">
        Pick an industry and compare three countries based on cost components. Use only numbers or percentages.
      </p>
      <p className="formula">Formula: True Tax Cost = Tax Rate + Pain Endured – Odds of Success – Tax Incentives</p>
      
      <div className="exercise-input-group">
        <label>Industry:</label>
        <input
          type="text"
          value={exercises.exercise4.industry}
          onChange={(e) => handleExerciseChange('exercise4', 'industry', e.target.value)}
          placeholder="Enter industry name"
        />
      </div>

      <div className="exercise-table">
        <div className="table-header">
          <div>Component</div>
          <div>Country 1</div>
          <div>Country 2</div>
          <div>Country 3</div>
        </div>
        
        <div className="table-row">
          <div>Country Name</div>
          <input
            type="text"
            value={exercises.exercise4.country1.name}
            onChange={(e) => handleExercise5Change(0, 'name', e.target.value)}
            placeholder="Country 1"
          />
          <input
            type="text"
            value={exercises.exercise4.country2.name}
            onChange={(e) => handleExercise5Change(1, 'name', e.target.value)}
            placeholder="Country 2"
          />
          <input
            type="text"
            value={exercises.exercise4.country3.name}
            onChange={(e) => handleExercise5Change(2, 'name', e.target.value)}
            placeholder="Country 3"
          />
        </div>
        
        <div className="table-row">
          <div>Tax rate (%)</div>
          <input
            type="number"
            value={exercises.exercise4.country1.taxRate}
            onChange={(e) => {
              handleExercise5Change(0, 'taxRate', e.target.value);
              calculateTrueTaxCost(0);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
          <input
            type="number"
            value={exercises.exercise4.country2.taxRate}
            onChange={(e) => {
              handleExercise5Change(1, 'taxRate', e.target.value);
              calculateTrueTaxCost(1);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
          <input
            type="number"
            value={exercises.exercise4.country3.taxRate}
            onChange={(e) => {
              handleExercise5Change(2, 'taxRate', e.target.value);
              calculateTrueTaxCost(2);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
        </div>
        
        <div className="table-row">
          <div>Pain endured (%)</div>
          <input
            type="number"
            value={exercises.exercise4.country1.painEndured}
            onChange={(e) => {
              handleExercise5Change(0, 'painEndured', e.target.value);
              calculateTrueTaxCost(0);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
          <input
            type="number"
            value={exercises.exercise4.country2.painEndured}
            onChange={(e) => {
              handleExercise5Change(1, 'painEndured', e.target.value);
              calculateTrueTaxCost(1);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
          <input
            type="number"
            value={exercises.exercise4.country3.painEndured}
            onChange={(e) => {
              handleExercise5Change(2, 'painEndured', e.target.value);
              calculateTrueTaxCost(2);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
        </div>
        
        <div className="table-row">
          <div>Odds of success (%)</div>
          <input
            type="number"
            value={exercises.exercise4.country1.oddsOfSuccess}
            onChange={(e) => {
              handleExercise5Change(0, 'oddsOfSuccess', e.target.value);
              calculateTrueTaxCost(0);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
          <input
            type="number"
            value={exercises.exercise4.country2.oddsOfSuccess}
            onChange={(e) => {
              handleExercise5Change(1, 'oddsOfSuccess', e.target.value);
              calculateTrueTaxCost(1);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
          <input
            type="number"
            value={exercises.exercise4.country3.oddsOfSuccess}
            onChange={(e) => {
              handleExercise5Change(2, 'oddsOfSuccess', e.target.value);
              calculateTrueTaxCost(2);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
        </div>
        
        <div className="table-row">
          <div>Tax incentive (%)</div>
          <input
            type="number"
            value={exercises.exercise4.country1.taxIncentive}
            onChange={(e) => {
              handleExercise5Change(0, 'taxIncentive', e.target.value);
              calculateTrueTaxCost(0);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
          <input
            type="number"
            value={exercises.exercise4.country2.taxIncentive}
            onChange={(e) => {
              handleExercise5Change(1, 'taxIncentive', e.target.value);
              calculateTrueTaxCost(1);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
          <input
            type="number"
            value={exercises.exercise4.country3.taxIncentive}
            onChange={(e) => {
              handleExercise5Change(2, 'taxIncentive', e.target.value);
              calculateTrueTaxCost(2);
            }}
            placeholder="0"
            min="0"
            max="100"
          />
        </div>
        
        <div className="table-row result-row">
          <div>True tax cost</div>
          <div className="result-value">{exercises.exercise4.country1.trueTaxCost || 'N/A'}</div>
          <div className="result-value">{exercises.exercise4.country2.trueTaxCost || 'N/A'}</div>
          <div className="result-value">{exercises.exercise4.country3.trueTaxCost || 'N/A'}</div>
        </div>
      </div>
    </div>
  );

  const renderExercise5 = () => (
    <div className="exercise-section">
      <div className="exercise-header">
        <h3>Exercise 5 – Your Government's Currency</h3>
        <button onClick={closeExercise} className="close-exercise-btn">✕</button>
      </div>
      <p className="exercise-instruction">
        Write down your country and the top three "currencies" your government values most.
      </p>
      
      <div className="exercise-input-group">
        <label>Country:</label>
        <input
          type="text"
          value={exercises.exercise5.country}
          onChange={(e) => handleExerciseChange('exercise5', 'country', e.target.value)}
          placeholder="Enter your country"
        />
      </div>

      <div className="currency-ranking">
        <div className="ranking-item">
          <label>Rank 1:</label>
          <input
            type="text"
            value={exercises.exercise5.currency1}
            onChange={(e) => handleExerciseChange('exercise5', 'currency1', e.target.value)}
            placeholder="Most valued currency"
          />
        </div>
        
        <div className="ranking-item">
          <label>Rank 2:</label>
          <input
            type="text"
            value={exercises.exercise5.currency2}
            onChange={(e) => handleExerciseChange('exercise5', 'currency2', e.target.value)}
            placeholder="Second most valued"
          />
        </div>
        
        <div className="ranking-item">
          <label>Rank 3:</label>
          <input
            type="text"
            value={exercises.exercise5.currency3}
            onChange={(e) => handleExerciseChange('exercise5', 'currency3', e.target.value)}
            placeholder="Third most valued"
          />
        </div>
      </div>
    </div>
  );

  const renderExercise6 = () => (
    <div className="exercise-section">
      <div className="exercise-header">
        <h3>Exercise 6 – Your Currency Over Life Stages</h3>
        <button onClick={closeExercise} className="close-exercise-btn">✕</button>
      </div>
      <p className="exercise-instruction">
        Fill in the currency that mattered most to you at each age stage.
      </p>
      
      <div className="age-currency-grid">
        <div className="age-currency-item">
          <label>Age 10:</label>
          <input
            type="text"
            value={exercises.exercise6.age10}
            onChange={(e) => handleExerciseChange('exercise6', 'age10', e.target.value)}
            placeholder="What mattered most at 10?"
          />
        </div>
        
        <div className="age-currency-item">
          <label>Age 20:</label>
          <input
            type="text"
            value={exercises.exercise6.age20}
            onChange={(e) => handleExerciseChange('exercise6', 'age20', e.target.value)}
            placeholder="What mattered most at 20?"
          />
        </div>
        
        <div className="age-currency-item">
          <label>Age 30:</label>
          <input
            type="text"
            value={exercises.exercise6.age30}
            onChange={(e) => handleExerciseChange('exercise6', 'age30', e.target.value)}
            placeholder="What mattered most at 30?"
          />
        </div>
        
        <div className="age-currency-item">
          <label>Age 40:</label>
          <input
            type="text"
            value={exercises.exercise6.age40}
            onChange={(e) => handleExerciseChange('exercise6', 'age40', e.target.value)}
            placeholder="What mattered most at 40?"
          />
        </div>
        
        <div className="age-currency-item">
          <label>Age 50:</label>
          <input
            type="text"
            value={exercises.exercise6.age50}
            onChange={(e) => handleExerciseChange('exercise6', 'age50', e.target.value)}
            placeholder="What mattered most at 50?"
          />
        </div>
        
        <div className="age-currency-item">
          <label>Age 60:</label>
          <input
            type="text"
            value={exercises.exercise6.age60}
            onChange={(e) => handleExerciseChange('exercise6', 'age60', e.target.value)}
            placeholder="What mattered most at 60?"
          />
        </div>
        
        <div className="age-currency-item">
          <label>Age 70:</label>
          <input
            type="text"
            value={exercises.exercise6.age70}
            onChange={(e) => handleExerciseChange('exercise6', 'age70', e.target.value)}
            placeholder="What mattered most at 70?"
          />
        </div>
        
        <div className="age-currency-item">
          <label>Age 80:</label>
          <input
            type="text"
            value={exercises.exercise6.age80}
            onChange={(e) => handleExerciseChange('exercise6', 'age80', e.target.value)}
            placeholder="What mattered most at 80?"
          />
        </div>
      </div>
    </div>
  );

  const renderExercise7 = () => (
    <div className="exercise-section">
      <div className="exercise-header">
        <h3>Exercise 7 – Quiz: Bulletproof Tax Structure</h3>
        <button onClick={closeExercise} className="close-exercise-btn">✕</button>
      </div>
      <p className="exercise-instruction">
        Answer these questions to test your understanding.
      </p>
      
      <div className="quiz-section">
        <div className="quiz-question">
          <p><strong>1. A bulletproof tax structure must be:</strong></p>
          <div className="quiz-options">
            <label>
              <input
                type="radio"
                name="question1"
                value="a"
                checked={exercises.exercise7.question1 === 'a'}
                onChange={(e) => handleExerciseChange('exercise7', 'question1', e.target.value)}
              />
              a) Complicated, Optimized, Exit-efficient
            </label>
            <label>
              <input
                type="radio"
                name="question1"
                value="b"
                checked={exercises.exercise7.question1 === 'b'}
                onChange={(e) => handleExerciseChange('exercise7', 'question1', e.target.value)}
              />
              b) Aligned, Simple, Exit-efficient ✅
            </label>
            <label>
              <input
                type="radio"
                name="question1"
                value="c"
                checked={exercises.exercise7.question1 === 'c'}
                onChange={(e) => handleExerciseChange('exercise7', 'question1', e.target.value)}
              />
              c) Aligned, Complex, Costly
            </label>
          </div>
        </div>

        <div className="quiz-question">
          <p><strong>2. "Exit-efficient" means:</strong></p>
          <div className="quiz-options">
            <label>
              <input
                type="radio"
                name="question2"
                value="a"
                checked={exercises.exercise7.question2 === 'a'}
                onChange={(e) => handleExerciseChange('exercise7', 'question2', e.target.value)}
              />
              a) You can leave the business structure with minimal tax friction ✅
            </label>
            <label>
              <input
                type="radio"
                name="question2"
                value="b"
                checked={exercises.exercise7.question2 === 'b'}
                onChange={(e) => handleExerciseChange('exercise7', 'question2', e.target.value)}
              />
              b) You can expand to another country
            </label>
            <label>
              <input
                type="radio"
                name="question2"
                value="c"
                checked={exercises.exercise7.question2 === 'c'}
                onChange={(e) => handleExerciseChange('exercise7', 'question2', e.target.value)}
              />
              c) You can change your product easily
            </label>
          </div>
        </div>

        <div className="quiz-question">
          <p><strong>3. True or False: If your structure is simple enough for an 8-year-old to understand, it passes part of the bulletproof test.</strong></p>
          <div className="quiz-options">
            <label>
              <input
                type="radio"
                name="question3"
                value={true}
                checked={exercises.exercise7.question3 === true}
                onChange={(e) => handleExerciseChange('exercise7', 'question3', e.target.value === 'true')}
              />
              True ✅
            </label>
            <label>
              <input
                type="radio"
                name="question3"
                value={false}
                checked={exercises.exercise7.question3 === false}
                onChange={(e) => handleExerciseChange('exercise7', 'question3', e.target.value === 'true')}
            />
              False
            </label>
          </div>
        </div>

        <div className="quiz-question">
          <p><strong>4. Which of these is NOT part of the bulletproof test?</strong></p>
          <div className="quiz-options">
            <label>
              <input
                type="radio"
                name="question4"
                value="a"
                checked={exercises.exercise7.question4 === 'a'}
                onChange={(e) => handleExerciseChange('exercise7', 'question4', e.target.value)}
              />
              a) Alignment
            </label>
            <label>
              <input
                type="radio"
                name="question4"
                value="b"
                checked={exercises.exercise7.question4 === 'b'}
                onChange={(e) => handleExerciseChange('exercise7', 'question4', e.target.value)}
              />
              b) Simplicity
            </label>
            <label>
              <input
                type="radio"
                name="question4"
                value="c"
                checked={exercises.exercise7.question4 === 'c'}
                onChange={(e) => handleExerciseChange('exercise7', 'question4', e.target.value)}
              />
              c) Profitability ✅
            </label>
            <label>
              <input
                type="radio"
                name="question4"
                value="d"
                checked={exercises.exercise7.question4 === 'd'}
                onChange={(e) => handleExerciseChange('exercise7', 'question4', e.target.value)}
              />
              d) Exit-efficiency
            </label>
          </div>
        </div>

        <div className="quiz-question">
          <p><strong>5. The bulletproof structure principle applies:</strong></p>
          <div className="quiz-options">
            <label>
              <input
                type="radio"
                name="question5"
                value="a"
                checked={exercises.exercise7.question5 === 'a'}
                onChange={(e) => handleExerciseChange('exercise7', 'question5', e.target.value)}
              />
              a) Only to international tax structures
            </label>
            <label>
              <input
                type="radio"
                name="question5"
                value="b"
                checked={exercises.exercise7.question5 === 'b'}
                onChange={(e) => handleExerciseChange('exercise7', 'question5', e.target.value)}
              />
              b) Only to domestic tax structures
            </label>
            <label>
              <input
                type="radio"
                name="question5"
                value="c"
                checked={exercises.exercise7.question5 === 'c'}
                onChange={(e) => handleExerciseChange('exercise7', 'question5', e.target.value)}
              />
              c) To both domestic and international structures ✅
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentExercise = () => {
    switch (currentExercise) {
      case 1: return renderExercise1();
      case 2: return renderExercise2();
      case 3: return renderExercise3();
      case 4: return renderExercise4();
      case 5: return renderExercise5();
      case 6: return renderExercise6();
      case 7: return renderExercise7();
      default: return null;
    }
  };

  const handleComplete = () => {
    if (onExercisesComplete) {
      onExercisesComplete(exercises);
    }
  };

  // If an exercise is open, show it
  if (currentExercise) {
    return (
      <div className="exercises-container">
        <div className="exercise-content">
          {renderCurrentExercise()}
        </div>
      </div>
    );
  }

  // Show the classroom interface
  return (
    <div className="exercises-container">
      <div className="exercises-header">
        <h2>📚 Tax Assessment Exercises</h2>
        <p>Complete these practical exercises to apply your tax structuring knowledge</p>
        
        {/* Email Input Section */}
        <div className="email-section" style={{
          margin: '20px 0',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>📧 Receive Progress Updates</h4>
          <p style={{ margin: '0 0 15px 0', fontSize: '0.9em', color: '#6c757d' }}>
            Enter your email to receive progress updates every time you complete an exercise
          </p>
          
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="email"
              placeholder="Enter your email address"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              style={{
                flex: '1',
                minWidth: '250px',
                padding: '10px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <button
              onClick={() => {
                if (userEmail.trim()) {
                  setEmailStatus({ type: 'info', message: '✅ Email saved! You\'ll receive progress updates.' });
                  setTimeout(() => setEmailStatus({ type: '', message: '' }), 3000);
                } else {
                  setEmailStatus({ type: 'warning', message: 'Please enter a valid email address.' });
                }
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Save Email
            </button>
          </div>
          
          {/* Email Status Display */}
          {emailStatus.message && (
            <div style={{
              marginTop: '10px',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '0.9em',
              backgroundColor: emailStatus.type === 'success' ? '#d4edda' : 
                             emailStatus.type === 'error' ? '#f8d7da' : 
                             emailStatus.type === 'warning' ? '#fff3cd' : '#d1ecf1',
              color: emailStatus.type === 'success' ? '#155724' : 
                     emailStatus.type === 'error' ? '#721c24' : 
                     emailStatus.type === 'warning' ? '#856404' : '#0c5460',
              border: `1px solid ${emailStatus.type === 'success' ? '#c3e6cb' : 
                                 emailStatus.type === 'error' ? '#f5c6cb' : 
                                 emailStatus.type === 'warning' ? '#ffeaa7' : '#bee5eb'}`
            }}>
              {emailStatus.message}
              {isSendingEmail && ' 📧 Sending...'}
            </div>
          )}
        </div>
        
        <div className="completion-status">
          <div className="completion-bar">
            <div 
              className="completion-fill" 
              style={{ width: `${getCompletionPercentage()}%` }}
            ></div>
          </div>
          <span className="completion-text">{getCompletionPercentage()}% Complete</span>
        </div>
      </div>

      <div className="classroom-grid">
        {exerciseMetadata.map((exercise) => (
          <div
            key={exercise.id}
            className={`exercise-card ${completionStatus[`exercise${exercise.id}`] ? 'completed' : ''}`}
            onClick={() => openExercise(exercise.id)}
            style={{ borderLeftColor: exercise.color }}
          >
            <div className="exercise-card-header">
              <div className="exercise-icon" style={{ backgroundColor: exercise.color }}>
                {exercise.icon}
              </div>
              <div className="exercise-status">
                {completionStatus[`exercise${exercise.id}`] ? (
                  <span className="status-completed">✓</span>
                ) : (
                  <span className="status-pending">○</span>
                )}
              </div>
            </div>
            
            <div className="exercise-card-content">
              <h3 className="exercise-title">{exercise.title}</h3>
              <p className="exercise-description">{exercise.description}</p>
              <div className="exercise-meta">
                <span className="exercise-time">⏱️ {exercise.estimatedTime}</span>
                <span className="exercise-number">#{exercise.id}</span>
              </div>
            </div>
            
            <div className="exercise-card-footer">
              <button className="start-exercise-btn">
                {completionStatus[`exercise${exercise.id}`] ? 'Review' : 'Start'}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="exercises-footer">
        <button onClick={onBack} className="back-button">
          ← Back to Assessment
        </button>
        
        <button 
          onClick={handleComplete} 
          className="complete-button"
          disabled={getCompletionPercentage() < 50}
        >
          Complete Exercises ({getCompletionPercentage()}%)
        </button>
      </div>
    </div>
  );
};

export default Exercises;
